import { useState } from "react";
import { Instagram, Mail, MessageCircle } from "lucide-react";
import { API, usePageTitle, useShop } from "@/context/ShopContext";
import Reveal from "@/components/Reveal";

export default function Contact() {
  usePageTitle("Contact");
  const { settings } = useShop();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const r = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24" data-testid="contact-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20">
        <Reveal>
          <p className="overline-label mb-4">Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.1]">
            We’d Love to Hear From You
          </h1>
          <p className="text-sm sm:text-base text-espresso/70 leading-relaxed mt-6 max-w-md">
            A question about a shade, a fabric, or an order — or simply a note to say
            hello. Write to us and we will reply with care.
          </p>
          <div className="mt-12 space-y-6">
            {[
              { icon: Instagram, label: "Instagram", note: `@${settings?.instagram || "Lamsahksa"}`, href: `https://www.instagram.com/${settings?.instagram || "Lamsahksa"}` },
              { icon: Mail, label: "Email", note: settings?.store_email || "Lamsahhijabs@gmail.com", href: `mailto:${settings?.store_email || "Lamsahhijabs@gmail.com"}` },
              settings?.whatsapp
                ? { icon: MessageCircle, label: "WhatsApp", note: `+${settings.whatsapp.replace(/^\+/, "")}`, href: `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}` }
                : { icon: MessageCircle, label: "WhatsApp", note: "Concierge chat coming soon" },
            ].map((c) => {
              const inner = (
                <>
                  <span className="w-11 h-11 border border-beige flex items-center justify-center">
                    <c.icon className="w-4 h-4 text-espresso" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-sm font-medium">{c.label}</p>
                    <p className="text-xs text-taupe mt-0.5">{c.note}</p>
                  </div>
                </>
              );
              return c.href ? (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-center gap-4 group hover:opacity-80 transition-opacity"
                  data-testid={`contact-channel-${c.label.toLowerCase()}`}
                >
                  {inner}
                </a>
              ) : (
                <div key={c.label} className="flex items-center gap-4" data-testid={`contact-channel-${c.label.toLowerCase()}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {status === "sent" ? (
            <div className="bg-white border border-beige p-10 md:p-14 text-center" data-testid="contact-success">
              <p className="font-serif text-2xl">Thank you.</p>
              <p className="text-sm text-espresso/70 mt-3 leading-relaxed">
                Your message has been received. We will be in touch soon.
              </p>
              <button onClick={() => setStatus("idle")} className="btn-secondary mt-8" data-testid="contact-send-another">
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-beige p-8 md:p-10 space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="overline-label block mb-3">Name</label>
                <input id="contact-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className="input-lux" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="contact-email" className="overline-label block mb-3">Email</label>
                <input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className="input-lux" placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="contact-message" className="overline-label block mb-3">Message</label>
                <textarea id="contact-message" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="input-lux resize-none" placeholder="How can we help?" />
              </div>
              {status === "error" && (
                <p className="text-sm text-rose" data-testid="contact-error">Something went wrong — please try again.</p>
              )}
              <button type="submit" data-testid="contact-submit" disabled={status === "sending"} className="btn-primary w-full">
                {status === "sending" ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
